import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { bold, num } from "../../utils/xlsx";
import {
  defaultPeriod,
  download,
  EmptyState,
  ExportButton,
  LoadingOverlay,
  money,
  PeriodFilter,
  periodLabel,
  ratio,
  REPORTS,
  sheetHeading,
  signTone,
  StatCard,
  validPeriod,
} from "./common";
import { blockRows, sideBySideRows, StatementSide, StatementSides } from "./Statement";

const line = (label, amount, strong = false) => ({ label, amount, level: 0, strong });

/** Trading A/c (gross profit) and Profit & Loss A/c (net profit) in Tally's two-sided layout. */
const buildSides = (report) => {
  const grossProfit = Number(report.grossProfit);
  const netProfit = Number(report.netProfit);
  const tradingTotal = Number(report.salesAccounts.total) + Number(report.directIncomes.total) + Number(report.closingStock) + (grossProfit < 0 ? -grossProfit : 0);
  const plTotal = (grossProfit >= 0 ? grossProfit : 0) + Number(report.indirectIncomes.total) + (netProfit < 0 ? -netProfit : 0);

  return {
    tradingDr: {
      title: "Trading A/c — Dr",
      rows: [
        line("Opening Stock", report.openingStock),
        ...blockRows(report.purchaseAccounts),
        ...blockRows(report.directExpenses),
        ...(grossProfit >= 0 ? [line("Gross Profit c/o", grossProfit, true)] : []),
      ],
      total: tradingTotal,
    },
    tradingCr: {
      title: "Trading A/c — Cr",
      rows: [
        ...blockRows(report.salesAccounts),
        ...blockRows(report.directIncomes),
        line("Closing Stock", report.closingStock),
        ...(grossProfit < 0 ? [line("Gross Loss c/o", -grossProfit, true)] : []),
      ],
      total: tradingTotal,
    },
    plDr: {
      title: "Profit & Loss A/c — Dr",
      rows: [
        ...(grossProfit < 0 ? [line("Gross Loss b/f", -grossProfit, true)] : []),
        ...blockRows(report.indirectExpenses),
        ...(netProfit >= 0 ? [line("Net Profit", netProfit, true)] : []),
      ],
      total: plTotal,
    },
    plCr: {
      title: "Profit & Loss A/c — Cr",
      rows: [
        ...(grossProfit >= 0 ? [line("Gross Profit b/f", grossProfit, true)] : []),
        ...blockRows(report.indirectIncomes),
        ...(netProfit < 0 ? [line("Net Loss", -netProfit, true)] : []),
      ],
      total: plTotal,
    },
  };
};

const ProfitAndLoss = () => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [applied, setApplied] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!validPeriod(period)) return;
    setLoading(true);
    try {
      setReport(await api.get(`${REPORTS}/profit-loss`, { fromDate: period.fromDate, toDate: period.toDate }));
      setApplied(period);
    } catch (error) {
      toast.error(error.message || "Failed to load profit & loss");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sides = report ? buildSides(report) : null;

  const exportExcel = () => {
    const rows = [
      ...sheetHeading("Profit & Loss A/c", periodLabel(applied)),
      ...sideBySideRows(sides.tradingDr, sides.tradingCr),
      [],
      ...sideBySideRows(sides.plDr, sides.plCr),
      [],
      [bold("Summary")],
      ["Net Sales", num(report.netSales)],
      ["Cost of Goods Sold", num(report.costOfGoodsSold)],
      ["Gross Profit", num(report.grossProfit)],
      ["Gross Profit %", num(report.grossProfitPercent)],
      ["Net Profit", num(report.netProfit)],
      ["Net Profit %", num(report.netProfitPercent)],
    ];
    download(`Profit and Loss ${periodLabel(applied)}`, [{ name: "Profit & Loss", rows, widths: [38, 16, 4, 38, 16] }]);
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Gross Profit = Net Sales + Direct Incomes − Cost of Goods Sold (Opening Stock + Purchases + Direct Expenses − Closing Stock). Net Profit =
        Gross Profit + Indirect Incomes − Indirect Expenses. Stock is valued at weighted-average purchase cost.
      </div>
      <div className="filters-bar">
        <PeriodFilter period={period} onChange={setPeriod} />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!report} />
      </div>

      {!report ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          <div className="stat-grid" style={{ marginTop: "16px" }}>
            <StatCard label="Net Sales" value={`₹${money(report.netSales)}`} />
            <StatCard label="Cost of Goods Sold" value={`₹${money(report.costOfGoodsSold)}`} />
            <StatCard
              label={Number(report.grossProfit) >= 0 ? "Gross Profit" : "Gross Loss"}
              value={`₹${money(Math.abs(report.grossProfit))}`}
              hint={`${ratio(report.grossProfitPercent, "%")} of sales`}
              tone={signTone(report.grossProfit)}
            />
            <StatCard
              label={Number(report.netProfit) >= 0 ? "Net Profit" : "Net Loss"}
              value={`₹${money(Math.abs(report.netProfit))}`}
              hint={`${ratio(report.netProfitPercent, "%")} of sales`}
              tone={signTone(report.netProfit)}
            />
          </div>
          <StatementSides>
            <StatementSide {...sides.tradingDr} />
            <StatementSide {...sides.tradingCr} />
          </StatementSides>
          <StatementSides>
            <StatementSide {...sides.plDr} />
            <StatementSide {...sides.plCr} />
          </StatementSides>
        </>
      )}
    </div>
  );
};

export default ProfitAndLoss;
